import { zodResolver } from '@hookform/resolvers/zod';
import type { Currency } from '@vy/types';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useQueryPolling } from '../../../../../../hooks/use-query-polling';
import { useUpdateInvoiceCurrency } from '../../../../../../hooks/requests/use-update-invoice-currency';
import { queryKeys, queryResources } from '../../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../../utils/vy-mutation';
import {
  ExchangeRateFormSchema,
  type ExchangeRateFormValues,
} from '../dynamic-invoice-header-types/exchange-rate-form-schema';
import { nullToUndefined } from '@vy/null';
import { roundTo } from '@vy/number';

type ExchangeRatesMatrix = Record<string, Record<string, number>>;

type UseExchangeRateFormParams = {
  invoiceId: string;
  contractCurrency: Currency;
  initialInvoiceCurrency: Currency | null;
  initialExchangeRate: number | null;
  exchangeRatesMatrix?: ExchangeRatesMatrix;
  onCloseDialog: () => void;
};

export const useExchangeRateForm = ({
  invoiceId,
  contractCurrency,
  initialInvoiceCurrency,
  initialExchangeRate,
  exchangeRatesMatrix,
  onCloseDialog,
}: UseExchangeRateFormParams) => {
  const queryClient = useQueryClient();
  const updateInvoiceCurrencyMutation = useUpdateInvoiceCurrency();
  const dynamicInvoiceQueryPolling = useQueryPolling({
    queryKey: queryKeys.dynamicInvoice(invoiceId),
    durationMs: 10000,
    intervalMs: 2000
  });

  const form = useForm<ExchangeRateFormValues>({
    resolver: zodResolver(ExchangeRateFormSchema),
    defaultValues: {
      invoiceCurrency: nullToUndefined(initialInvoiceCurrency),
      exchangeRate: nullToUndefined(initialExchangeRate),
    },
  });

  const handleSubmit = form.handleSubmit(async (formValues: ExchangeRateFormValues) => {
    dynamicInvoiceQueryPolling.stop();

    await runMutation(
      updateInvoiceCurrencyMutation,
      {
        invoiceId,
        updateInvoiceCurrencyRequest: {
          modifiedCurrency: formValues.invoiceCurrency,
          modifiedExchangeRate: formValues.exchangeRate,
        },
      },
      {
        pending: 'Updating currency...',
        success: 'Currency updated',
        error: 'Failed to update currency',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: ({ queryKey }) =>
              [queryResources.billingCyclePage, queryResources.billingCycleInvoices, queryResources.dynamicInvoice, invoiceId].some((key) =>
                queryKey.includes(key),
              ),
          });
          dynamicInvoiceQueryPolling.start();
          onCloseDialog();
          form.reset({
            invoiceCurrency: formValues.invoiceCurrency,
            exchangeRate: formValues.exchangeRate,
          });
        },
      },
    );
  });

  const watchedInvoiceCurrency = form.watch('invoiceCurrency');
  const exchangeRateFromMatrix = exchangeRatesMatrix?.[watchedInvoiceCurrency]?.[contractCurrency] ?? 1;
  const placeholderExchangeRate = roundTo(1 / exchangeRateFromMatrix, 5)

  const handleCurrencyChange = (selectedCurrency: Currency) => {
    form.setValue('invoiceCurrency', selectedCurrency);
    form.setValue('exchangeRate', null);
  };

  const handleResetToOriginal = async () => {
    dynamicInvoiceQueryPolling.stop();

    await runMutation(
      updateInvoiceCurrencyMutation,
      {
        invoiceId,
        updateInvoiceCurrencyRequest: {
          modifiedCurrency: null,
          modifiedExchangeRate: null,
        },
      },
      {
        pending: 'Resetting currency...',
        success: 'Currency reset to original',
        error: 'Failed to reset currency',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: ({ queryKey }) =>
              [queryResources.billingCyclePage, queryResources.billingCycleInvoices, queryResources.dynamicInvoice, invoiceId].some((key) =>
                queryKey.includes(key),
              ),
          });
          dynamicInvoiceQueryPolling.start();
          onCloseDialog();
          form.reset();
        },
      },
    );
  };

  return {
    form,
    handleSubmit,
    handleCurrencyChange,
    handleResetToOriginal,
    placeholderExchangeRate,
  };
};
